import './Input.css';

import { ChangeEvent, FocusEvent, MouseEvent, Ref, KeyboardEvent } from 'react';
import { IonIcon, IonLabel } from '@ionic/react';
import clsx from 'clsx';
import { FilterError } from '../../types/types';
import InputErrorMessage from '../Manager/InputErrorMessage/InputErrorMessage';

export type InputType =
  | 'text'
  | 'number'
  | 'password'
  | 'email'
  | 'tel'
  | 'date'
  | 'time'
  | 'datetime-local';

interface InputProps {
  onChange?: (value: string, event: ChangeEvent<HTMLInputElement>) => void;
  value: string;
  name?: string;
  label?: string;
  error?: FilterError;
  type?: InputType;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  icon?: string;
  inputRef?: Ref<HTMLInputElement>;
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  onClick?: (event: MouseEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  hideErrorText?: boolean;
  decorator?: string;
  min?: number;
  max?: number;
  // Virtual keyboard hint (e.g. 'numeric'/'decimal') independent of input type.
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  // Defaults to 'off'. Password form fields pass 'new-password' so Firefox's
  // password manager (which ignores 'off' on password fields) won't autofill
  // the saved app login into them.
  autoComplete?: string;
}

const INPUT_DECORATOR_CLASSNAME = 'input-decorator';

export default function Input({
  onChange,
  error = null,
  value,
  type = 'text',
  name,
  label,
  required,
  disabled = false,
  className,
  placeholder,
  icon,
  inputRef = undefined,
  onBlur = () => {},
  onFocus = () => {},
  onClick = () => {},
  onKeyDown = () => {},
  hideErrorText = false,
  decorator,
  min,
  max,
  inputMode,
  autoComplete = 'off',
}: InputProps) {
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      const value = e.target.value as string;
      onChange(value, e);
    }
  };

  return (
    <div
      className={clsx(
        'input-container',
        className,
        icon && 'input-container-with-icon'
      )}
    >
      {label && <IonLabel>{label}</IonLabel>}
      {icon && (
        <IonIcon icon={icon} aria-hidden="true" className="input-icon" />
      )}
      <div className="input-field">
        <input
          {...(label && {
            title: label,
          })}
          required={required}
          min={min}
          max={max}
          name={name}
          type={type}
          inputMode={inputMode}
          value={value}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={disabled}
          {...(error && {
            'aria-invalid': true,
            'aria-errormessage': `error-${name}`,
          })}
          ref={inputRef}
          {...(placeholder && {
            placeholder,
          })}
          autoComplete={autoComplete}
          onClick={onClick}
          // Reserve inner space when a runtime decorator is rendered on the right side.
          className={clsx(
            'input-element',
            error && 'input-error',
            decorator && 'has-decorator'
          )}
        />
        {decorator && (
          <span className={INPUT_DECORATOR_CLASSNAME} aria-hidden="true">
            {decorator}
          </span>
        )}
      </div>
      {!hideErrorText && <InputErrorMessage error={error} />}
    </div>
  );
}
